// src/components/DRE/DREReport.js - Atualizado com botão de colapsar no topo
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc
} from "firebase/firestore";
import MainLayout from "../Layout/MainLayout";
import "./DRE.css";
import { useUserContext } from "../../contexts/UserContext"; // Importar o contexto de usuário

// Importar serviços refatorados - REUTILIZAÇÃO DE CÓDIGO
import { 
  loadAvailablePeriods,
  loadCategoryMappings,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

import { checkTransactionCategorization } from "../../services/categorizationService";

const DREReport = () => {
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true); // Separate loading state for DRE data
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [user, setUser] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [dreData, setDreData] = useState({});
  const [unmappedTransactions, setUnmappedTransactions] = useState([]);
  const [error, setError] = useState("");
  const [categoryOrder, setCategoryOrder] = useState({});
  const [categoryMappings, setCategoryMappings] = useState({});
  const [nonCategorizedExpanded, setNonCategorizedExpanded] = useState(false);
  const [debugMode, setDebugMode] = useState(false); // Para ativar logs detalhados
  const [businessModel, setBusinessModel] = useState(null); // Adicionar estado para modelo de negócio
  
  // Estado para controlar o colapso do relatório
  const [collapsedReport, setCollapsedReport] = useState(false);
  
  // Obter o tipo de conta ativo e outras informações do contexto do usuário
  const { activeAccountType, getAccountSubpath, activeBusinessProfileId } = useUserContext();

  // Função para alternar o estado de colapso do relatório
  const toggleReportCollapse = () => {
    setCollapsedReport(!collapsedReport);
  };

  // Função para normalizar categorias principais e garantir compatibilidade com a estrutura do DRE
  const normalizeMainCategory = (categoryName) => {
    if (!categoryName) return null;
    
    // Verificar se estamos usando o modelo pessoal ou conta pessoal
    if (businessModel === "pessoal" || activeAccountType === 'personal') {
      return categoryName; // No modelo pessoal, usamos as categorias como estão
    }
    
    // Normalizar para minúsculas para comparação
    const normalized = categoryName.trim().toLowerCase();
    
    // IMPORTANTE: Verificar padrões específicos ANTES dos genéricos
    // Verificar primeiro se é aporte de sócios
    if (normalized.includes('aporte') || 
        (normalized.includes('capital') && normalized.includes('social')) ||
        normalized.includes('integralização') ||
        normalized.includes('investimento dos sócios') ||
        normalized.includes('aporte de capital')) {
      return "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS";
    }
    
    // Depois verificar outras categorias específicas
    if (normalized.includes('deduções') || normalized.includes('deducoes')) {
      return "(-) DEDUÇÕES DA RECEITA";
    }
    
    if (normalized.includes('outras receitas')) {
      return "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS";
    }
    
    if (businessModel === "consultorioOdontologico") {
      if (normalized.includes('csp') || normalized.includes('custos dos serviços prestados') || normalized.includes('custos')) { // Ajustar para termos de CSP
        return "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)"; // <- Alterar aqui
      }
    } else { // Outros modelos de negócio
      if (normalized.includes('cmv') || normalized.includes('custos')) {
        return "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)";
      }
    }
    
    if (normalized.includes('despesa') || normalized.includes('despesas')) {
      if (normalized.includes('sócio') || normalized.includes('socio')) {
        return "(-) DESPESAS COM SÓCIOS";
      }
      return "(-) DESPESAS OPERACIONAIS";
    }
    
    if (normalized.includes('investimento') && !normalized.includes('sócio') && !normalized.includes('socio')) {
      return "(-) INVESTIMENTOS";
    }
    
    // Verificar receita por último para evitar falsos positivos
    if (normalized.includes('receita')) {
      return "RECEITA";
    }
    
    // Logs de debug para a normalização
    if (debugMode) {
      console.log(`Normalização: "${categoryName}" -> "${normalized}" -> não mapeado`);
    }
    
    // Se não encontrar correspondência, retorna a categoria original
    return categoryName;
  };

  // Função para verificar se uma descrição ou categoria se refere a aporte de sócios
  const isAporteDeSocios = (text) => {
    if (!text) return false;
    
    const normalized = text.trim().toLowerCase();
    return normalized.includes('aporte') || 
           (normalized.includes('capital') && normalized.includes('social')) ||
           normalized.includes('integralização') ||
           normalized.includes('investimento dos sócios') ||
           normalized.includes('aporte de capital');
  };

  // Função para alternar a visibilidade das transações não categorizadas
  const toggleNonCategorizedSection = () => {
    setNonCategorizedExpanded(!nonCategorizedExpanded);
  };

  // Carregar usuário e períodos disponíveis
  useEffect(() => {
    const loadUserAndPeriods = async () => {
      try {
        setLoading(true);
        setDataLoading(true);
        
        if (auth.currentUser) {
          setUser(auth.currentUser);
          
          // Carregar o modelo de negócio do usuário com base no tipo de conta ativo
          try {
            const userCategoriesDoc = await getDoc(doc(db, "userCategories", auth.currentUser.uid));
            if (userCategoriesDoc.exists()) {
              // Determinar o modelo de negócio baseado no tipo de conta ativo
              if (activeAccountType === 'personal') {
                // Para conta pessoal, usar o modelo pessoal
                setBusinessModel("pessoal");
              } else {
                // Para conta empresarial, usar o modelo salvo ou padrão
                setBusinessModel(userCategoriesDoc.data().businessModel || "restaurante");
              }
            }
          } catch (err) {
            console.error("Erro ao carregar modelo de negócio:", err);
            // Definir modelo padrão baseado no tipo de conta
            setBusinessModel(activeAccountType === 'personal' ? "pessoal" : "restaurante");
          }
          
          // Usar serviço refatorado para carregar períodos disponíveis
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);
          
          // Verificar se existe um período salvo no localStorage
          let savedPeriod = localStorage.getItem('selectedPeriod');
          let periodToUse;
          
          if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
            periodToUse = savedPeriod;
          } else if (availablePeriods.length > 0) {
            periodToUse = availablePeriods[0].value;
            localStorage.setItem('selectedPeriod', periodToUse);
          }
          
          if (periodToUse) {
            setSelectedPeriod(periodToUse);
          }
          
          // Carregar mapeamentos de categorias usando serviço refatorado
          const mappings = await loadCategoryMappings();
          setCategoryMappings(mappings);
          
          // Carregar ordem das categorias - Mantido aqui porque é específico para o DRE
          try {
            const userCategoriesRef = activeAccountType === 'personal' 
              ? doc(db, "userCategories", auth.currentUser.uid, "personal", "categories")
              : doc(db, "userCategories", auth.currentUser.uid);
            
            const userCategoriesDoc = await getDoc(userCategoriesRef);
            if (userCategoriesDoc.exists()) {
              const data = userCategoriesDoc.data();
              // Se for conta pessoal, usar ordem fixa para categorias pessoais
              if (activeAccountType === 'personal') {
                setCategoryOrder({
                  "💸 Receitas": 1,
                  "🧾 Despesas Fixas": 2,
                  "🍞 Despesas Variáveis": 3,
                  "💳 Pagamentos e Transferências": 4,
                  "NÃO CATEGORIZADO": 5
                });
              } else {
                // Para conta empresarial, usar a ordem salva
                setCategoryOrder(data.categoryOrder || {});
              }
            }
          } catch (err) {
            console.error("Erro ao carregar ordem das categorias:", err);
          }
          
          // Mark initial load as complete but keep loading state active
          setInitialLoadComplete(true);
        }
      } catch (error) {
        console.error("Erro ao carregar períodos:", error);
        setError("Não foi possível carregar os períodos disponíveis.");
        setLoading(false);
        setDataLoading(false);
      }
    };
    
    loadUserAndPeriods();
  }, [activeAccountType]); // Adicionar activeAccountType como dependência

  // Reset não categorizado expandido quando mudar de período
  useEffect(() => {
    setNonCategorizedExpanded(false);
  }, [selectedPeriod]);

  // Carregar dados do DRE quando o período selecionado mudar
  useEffect(() => {
    if (selectedPeriod && user && initialLoadComplete) {
      loadDREData(selectedPeriod);
    }
  }, [selectedPeriod, user, categoryMappings, initialLoadComplete, businessModel, activeAccountType]);

  // Função para carregar os dados do DRE
  const loadDREData = async (period) => {
    try {
      setDataLoading(true);
      
      // Inicializar a estrutura do DRE com base no modelo de negócio e tipo de conta
      let dreStructure = {};
      let mainCategories = [];
      const unmappedItems = []; // Inicializar array para itens não mapeados
      
      // Defina a estrutura com base no modelo de negócio ou tipo de conta
      if (businessModel === "pessoal" || activeAccountType === 'personal') {
        // Estrutura para modelo pessoal ou conta pessoal
        mainCategories = [
          "💸 Receitas",
          "🧾 Despesas Fixas",
          "🍞 Despesas Variáveis",
          "💳 Pagamentos e Transferências",
          "NÃO CATEGORIZADO"
        ];
      } else if (businessModel === "consultorioOdontologico") { // Adicionar esta condição
        mainCategories = [
          "RECEITA",
          "(-) DEDUÇÕES DA RECEITA",
          "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
          "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)", // <- Alterar aqui
          "(-) DESPESAS OPERACIONAIS",
          "(-) DESPESAS COM SÓCIOS",
          "(-) INVESTIMENTOS",
          "NÃO CATEGORIZADO"
        ];
      } else { // Modelo padrão (restaurante, etc.)
        mainCategories = [
          "RECEITA",
          "(-) DEDUÇÕES DA RECEITA",
          "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS",
          "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)",
          "(-) DESPESAS OPERACIONAIS",
          "(-) DESPESAS COM SÓCIOS",
          "(-) INVESTIMENTOS",
          "NÃO CATEGORIZADO"
        ];
      }
      
      // Inicializar estrutura para cada categoria principal
      mainCategories.forEach(category => {
        dreStructure[category] = {
          total: 0,
          items: {},
          rawTotal: 0 // Para armazenar o valor bruto sem alterações
        };
      });

      // Identificar mapeamentos específicos por ID de transação
      const specificMappings = {};
      
      // Identificamos todos os mapeamentos específicos (para transações únicas)
      Object.entries(categoryMappings).forEach(([key, mapping]) => {
        if (mapping.isSpecificMapping && mapping.transactionId) {
          specificMappings[mapping.transactionId] = mapping;
        }
      });
      
      // PARTE 1: Processar arquivos OFX usando o serviço refatorado
      // Obter transações para o período selecionado
      const { transactions: allTransactions } = await loadTransactionsForPeriod(period);
      
      // Debug: listar todas as transações
      if (debugMode) {
        console.log(`Total de transações carregadas: ${allTransactions.length}`);
      }
      
      // Contador de categorias para debugar
      const categoryCounter = {};
      
      // Processar cada transação
      allTransactions.forEach(transaction => {
        let mainCategory, subCategory;
        let categorized = false;
        let isAporte = false;
        
        // Debug: verificar cada transação
        if (debugMode) {
          console.log(`Processando transação: ${transaction.id} - ${transaction.description} - ${transaction.amount}`);
        }
        
        // Verificar se é um aporte de sócios pela descrição
        if (businessModel !== "pessoal" && activeAccountType !== 'personal' && isAporteDeSocios(transaction.description)) {
          isAporte = true;
          if (debugMode) {
            console.log(`Transação identificada como aporte de sócios: ${transaction.description}`);
          }
        }
        
        // Primeiro verificar se existe um mapeamento específico para esta transação
        if (specificMappings[transaction.id]) {
          const mapping = specificMappings[transaction.id];
          const originalCategory = mapping.groupName;
          
          // Se for aporte de sócios, forçar para a categoria correta
          if (businessModel !== "pessoal" && activeAccountType !== 'personal' && 
              (isAporte || isAporteDeSocios(mapping.categoryName) || isAporteDeSocios(mapping.groupName))) {
            mainCategory = "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS";
            subCategory = "Aporte de Sócios";
          } else {
            mainCategory = normalizeMainCategory(mapping.groupName);
            subCategory = mapping.categoryName;
          }
          
          categorized = true;
          
          if (debugMode) {
            console.log(`Mapeamento específico encontrado: ${originalCategory} -> ${mainCategory}`);
          }
        } 
        // Verificar categorização usando o serviço refatorado
        else {
          const result = checkTransactionCategorization(transaction, categoryMappings);
          if (result.isCategorized) {
            const originalCategory = result.mainCategory;
            
            // Se for aporte de sócios, forçar para a categoria correta
            if (businessModel !== "pessoal" && activeAccountType !== 'personal' && 
                (isAporte || isAporteDeSocios(result.subCategory) || isAporteDeSocios(result.mainCategory))) {
              mainCategory = "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS";
              subCategory = "Aporte de Sócios";
            } else {
              mainCategory = normalizeMainCategory(result.mainCategory);
              subCategory = result.subCategory;
            }
            
            categorized = true;
            
            if (debugMode) {
              console.log(`Categorização encontrada: ${originalCategory} -> ${mainCategory}`);
            }
          }
        }
        
        if (categorized && mainCategory && dreStructure[mainCategory]) {
          // Contagem para debug
          if (!categoryCounter[mainCategory]) {
            categoryCounter[mainCategory] = 0;
          }
          categoryCounter[mainCategory]++;
          
          if (!dreStructure[mainCategory].items[subCategory]) {
            dreStructure[mainCategory].items[subCategory] = 0;
          }
          dreStructure[mainCategory].items[subCategory] += transaction.amount;
          dreStructure[mainCategory].total += transaction.amount;
          dreStructure[mainCategory].rawTotal += transaction.amount; // Armazenar o valor bruto
          
          if (debugMode) {
            console.log(`Transação adicionada à categoria ${mainCategory}.${subCategory}: ${transaction.amount}`);
          }
        } else {
          unmappedItems.push({
            id: transaction.id,
            ...transaction,
            period: period
          });
          
          const description = transaction.description || "Transação sem descrição";
          if (!dreStructure["NÃO CATEGORIZADO"].items[description]) {
            dreStructure["NÃO CATEGORIZADO"].items[description] = 0;
          }
          dreStructure["NÃO CATEGORIZADO"].items[description] += transaction.amount;
          dreStructure["NÃO CATEGORIZADO"].total += transaction.amount;
          dreStructure["NÃO CATEGORIZADO"].rawTotal += transaction.amount; // Armazenar o valor bruto
          
          if (debugMode) {
            console.log(`Transação não categorizada: ${transaction.description} - ${transaction.amount}`);
            if (categorized) {
              console.log(`Motivo: categoria ${mainCategory} não encontrada na estrutura DRE`);
            } else {
              console.log(`Motivo: transação não categorizada`);
            }
          }
        }
      });
      
      // Debug: mostrar contagem de categorias
      if (debugMode) {
        console.log("Contagem de transações por categoria:");
        console.log(categoryCounter);
      }
      
      // PARTE 2: Processar entradas manuais de dinheiro
      // Esta parte é específica do DRE e ainda precisa ser implementada aqui
      
      try {
        // ALTERADO: Usar getAccountSubpath() em vez de lógica hardcoded
        const accountSubpath = getAccountSubpath();
        
        // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
        let userId = auth.currentUser.uid;
        
        // Verificar no documento do usuário se é um colaborador
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists() && userDoc.data().isCollaborator === true) {
          // Verificar informações do workspace
          const invitedBy = userDoc.data().invitedBy;
          const originalInvitation = userDoc.data().originalInvitation || {};
          const profileId = originalInvitation.profileId;
          
          // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
          if (activeBusinessProfileId === profileId && invitedBy) {
            userId = invitedBy; // Usar o ID do proprietário
            console.log(`Colaborador detectado. Carregando entradas do proprietário: ${userId}`);
          }
        }
        
        const cashEntriesQuery = query(
          collection(db, "cashEntries", userId, accountSubpath),
          where("period", "==", period)
        );
        
        const cashEntriesSnapshot = await getDocs(cashEntriesQuery);
        
        console.log(`Carregadas ${cashEntriesSnapshot.size} entradas manuais de dinheiro`);
        
        // Processar cada entrada manual e adicionar à estrutura DRE
        cashEntriesSnapshot.forEach(doc => {
          const entry = doc.data();
          
          // Verificar se a entrada tem categoria e valor
          if (entry.categoryPath && typeof entry.amount === 'number') {
            const pathParts = entry.categoryPath.split('.');
            
            if (pathParts.length >= 2) {
              let mainCategory = pathParts[0];
              const subCategory = entry.category || pathParts[1];
              
              if (businessModel !== "pessoal" && activeAccountType !== 'personal') {
                // Verificar se é aporte de sócios
                if (isAporteDeSocios(mainCategory) || isAporteDeSocios(subCategory)) {
                  mainCategory = "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS";
                } else {
                  // Normalizar a categoria principal para garantir consistência
                  mainCategory = normalizeMainCategory(mainCategory);
                }
              }
              
              // Verificar se a categoria principal existe na estrutura DRE
              if (dreStructure[mainCategory]) {
                // Adicionar à categoria principal
                if (!dreStructure[mainCategory].items[subCategory]) {
                  dreStructure[mainCategory].items[subCategory] = 0;
                }
                
                dreStructure[mainCategory].items[subCategory] += entry.amount;
                dreStructure[mainCategory].total += entry.amount;
                dreStructure[mainCategory].rawTotal += entry.amount; // Armazenar o valor bruto
                
                console.log(`Entrada manual adicionada: ${formatCurrency(entry.amount)} em ${mainCategory}.${subCategory}`);
              }
            }
          }
        });
      } catch (error) {
        console.error("Erro ao processar entradas manuais:", error);
      }
      
      // Calcular somatório total de todas as transações (para verificação)
      let sumOfAllTransactions = 0;
      mainCategories.forEach(category => {
        sumOfAllTransactions += dreStructure[category].rawTotal;
      });
      
      console.log("Soma total de todas as transações (bruto):", sumOfAllTransactions);
      
      // Debug: mostrar a estrutura completa do DRE antes de calcular resultados
      if (debugMode) {
        console.log("Estrutura DRE completa:", dreStructure);
      }
      
      // Calcular resultados com base no modelo de negócio
      // Usar activeAccountType para determinar o modelo a ser utilizado
      const effectiveModel = activeAccountType === 'personal' ? "pessoal" : businessModel;
      const results = calculateDREResults(dreStructure, effectiveModel);
      
      setDreData({
        categories: dreStructure,
        results: results,
        totalTransactions: sumOfAllTransactions
      });
      
      setUnmappedTransactions(unmappedItems);
    } catch (error) {
      console.error("Erro ao carregar dados do DRE:", error);
      setError("Não foi possível carregar os dados do DRE para o período selecionado.");
    } finally {
      // Now that all data is loaded, turn off both loading indicators
      setLoading(false);
      setDataLoading(false);
    }
  };

  // Função para calcular os resultados do DRE
  const calculateDREResults = (dreStructure, effectiveBusinessModel) => {
    const results = {};
    
    // Verificar se estamos usando o modelo pessoal
    if (effectiveBusinessModel === "pessoal" || activeAccountType === 'personal') {
      // Cálculos para modelo pessoal
      results.receitaTotal = dreStructure["💸 Receitas"]?.rawTotal || 0;
      const despesasFixas = dreStructure["🧾 Despesas Fixas"]?.rawTotal || 0;
      const despesasVariaveis = dreStructure["🍞 Despesas Variáveis"]?.rawTotal || 0;
      const pagamentosTransferencias = dreStructure["💳 Pagamentos e Transferências"]?.rawTotal || 0;
      const naoCategorizado = dreStructure["NÃO CATEGORIZADO"]?.rawTotal || 0;
      
      results.despesasTotal = despesasFixas + despesasVariaveis;
      results.pagamentosTotal = pagamentosTransferencias;
      
      // Soma direta de todas as categorias, incluindo não categorizadas
      results.saldoFinal = results.receitaTotal + despesasFixas + despesasVariaveis + 
                           pagamentosTransferencias + naoCategorizado;
      
      return results;
    }
    
    // Cálculos para modelos de negócio tradicionais
    results.receitaBruta = dreStructure["RECEITA"]?.rawTotal || 0;
    const deducoes = dreStructure["(-) DEDUÇÕES DA RECEITA"]?.rawTotal || 0;
    results.receitaLiquida = results.receitaBruta + deducoes;
    
    let custos;
    if (effectiveBusinessModel === "consultorioOdontologico") {
      custos = dreStructure["(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)"]?.rawTotal || 0;
    } else {
      custos = dreStructure["(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"]?.rawTotal || 0;
    }
    results.lucroBruto = results.receitaLiquida + custos; // Custos são negativos, então a soma funciona.
    
    const despesasOperacionais = dreStructure["(-) DESPESAS OPERACIONAIS"]?.rawTotal || 0;
    results.resultadoOperacional = results.lucroBruto + despesasOperacionais;
    
    // Calcular o valor das despesas de sócios a ser excluído
    let despesasDeSociosValue = 0;
    if (dreStructure["(-) DESPESAS COM SÓCIOS"] && 
        dreStructure["(-) DESPESAS COM SÓCIOS"].items) {
      for (const [subCategory, value] of Object.entries(dreStructure["(-) DESPESAS COM SÓCIOS"].items)) {
        if (subCategory.toLowerCase() === 'despesas de sócios') {
          despesasDeSociosValue = value;
          break;
        }
      }
    }
    
    // Usar o valor total da categoria menos o valor da subcategoria específica
    const despesasSociosAjustadas = (dreStructure["(-) DESPESAS COM SÓCIOS"]?.rawTotal || 0) - despesasDeSociosValue;
    results.resultadoAntesIR = results.resultadoOperacional + despesasSociosAjustadas;
    
    const investimentos = dreStructure["(-) INVESTIMENTOS"]?.rawTotal || 0;
    const naoCategorizado = dreStructure["NÃO CATEGORIZADO"]?.rawTotal || 0;
    const outrasReceitas = dreStructure["(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS"]?.rawTotal || 0;
    
    // CORREÇÃO: Calcular o resultado líquido de forma consistente
    // Incluindo todas as categorias relevantes diretamente
    results.resultadoLiquido = results.receitaBruta + deducoes + custos + 
                             despesasOperacionais + despesasSociosAjustadas + 
                             investimentos + naoCategorizado;
    
    // Adicionar logs para debug
    console.log("DRE - Debug - Receita Bruta:", results.receitaBruta);
    console.log("DRE - Debug - Deduções:", deducoes);
    console.log("DRE - Debug - Custos:", custos);
    console.log("DRE - Debug - Despesas Operacionais:", despesasOperacionais);
    console.log("DRE - Debug - Despesas Sócios Ajustadas:", despesasSociosAjustadas);
    console.log("DRE - Debug - Investimentos:", investimentos);
    console.log("DRE - Debug - Não Categorizado:", naoCategorizado);
    console.log("DRE - Debug - Outras Receitas (excluídas):", outrasReceitas);
    console.log("DRE - Debug - Resultado Líquido Calculado:", results.resultadoLiquido);
    
    return results;
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para obter a classe CSS baseada no valor
  const getValueClass = (value, isNegativeNormal = false) => {
    if (value === 0) return "value-zero";
    if (isNegativeNormal) {
      return value < 0 ? "value-negative" : "value-positive";
    } else {
      return value < 0 ? "value-positive" : "value-negative";
    }
  };

  // Função para alternar o modo de debug
  const toggleDebugMode = () => {
    setDebugMode(!debugMode);
    console.log(`Modo de debug ${!debugMode ? 'ativado' : 'desativado'}`);
  };

  // Ordenar categorias principais
  const sortedMainCategories = Object.keys(dreData.categories || {}).sort((a, b) => {
    // Para o modelo pessoal ou conta pessoal, manter a ordem original
    if (businessModel === "pessoal" || activeAccountType === 'personal') {
      const order = {
        "💸 Receitas": 1,
        "🧾 Despesas Fixas": 2,
        "🍞 Despesas Variáveis": 3,
        "💳 Pagamentos e Transferências": 4,
        "NÃO CATEGORIZADO": 5
      };
      return (order[a] || 999) - (order[b] || 999);
    }
    
    // Para modelos de negócio tradicionais, usar a ordem salva no Firebase
    return (categoryOrder[a] || 999) - (categoryOrder[b] || 999);
  });

  // New comprehensive loading check
  if (loading || (initialLoadComplete && dataLoading)) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{initialLoadComplete ? "Carregando dados do DRE..." : "Carregando períodos disponíveis..."}</p>
        </div>
      </MainLayout>
    );
  }

  // Renderização para o modelo pessoal ou conta pessoal
  if (businessModel === "pessoal" || activeAccountType === 'personal') {
    return (
      <MainLayout userName={user?.displayName || "Usuário"}>
        <div className="dre-container">
          {/* Seletor de período com botão de colapsar no topo */}
          <div className="period-selector-container">
            <div className="period-selector-header">
              <h2>Relatório Financeiro Pessoal</h2>
              <div className="period-actions">
                <button 
                  className={`collapse-button-top ${collapsedReport ? 'active' : ''}`} 
                  onClick={toggleReportCollapse}
                >
                  {collapsedReport ? 'Expandir' : 'Colapsar'}
                </button>
                
                <div className="period-dropdown">
                  <label htmlFor="period-select">Período: </label>
                  <select
                    id="period-select"
                    value={selectedPeriod}
                    onChange={(e) => {
                      const period = e.target.value;
                      setSelectedPeriod(period);
                      localStorage.setItem('selectedPeriod', period);
                    }}
                    disabled={dataLoading}
                  >
                    {periods.length === 0 ? (
                      <option value="">Nenhum período disponível</option>
                    ) : (
                      periods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {unmappedTransactions.length > 0 && (
            <div className="warning-message">
              <p>
                <strong>Atenção:</strong> Existem {unmappedTransactions.length} transações não 
                categorizadas neste período. Para um relatório mais preciso, categorize estas transações na página de transações.
              </p>
            </div>
          )}
          
          {/* Botão de debug oculto - só visível se inspecionar ou clicar na área específica */}
          <div 
            onClick={toggleDebugMode} 
            style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              width: '20px', 
              height: '20px', 
              cursor: 'pointer',
              opacity: 0.2
            }}
          ></div>
          
          {debugMode && (
            <div className="debug-info" style={{ 
              margin: '10px 0', 
              padding: '10px', 
              background: '#f8f9fa', 
              border: '1px solid #ddd',
              fontSize: '12px'
            }}>
              <strong>Modo Debug Ativado</strong> - Verifique o console do navegador para detalhes
            </div>
          )}
          
          <div className="dre-content">
            <div className="dre-summary">
              <h2>Resumo Financeiro</h2>
              <div className="summary-cards">
                <div className="summary-card">
                  <h3>Receitas</h3>
                  <div className={`summary-value ${getValueClass(dreData.results?.receitaTotal || 0, true)}`}>
                    {formatCurrency(dreData.results?.receitaTotal || 0)}
                  </div>
                </div>
                
                <div className="summary-card">
                  <h3>Despesas</h3>
                  <div className={`summary-value ${getValueClass(dreData.results?.despesasTotal || 0, true)}`}>
                    {formatCurrency(dreData.results?.despesasTotal || 0)}
                  </div>
                </div>
                
                <div className="summary-card">
                  <h3>Saldo</h3>
                  <div className={`summary-value ${getValueClass(dreData.results?.saldoFinal || 0, true)}`}>
                    {formatCurrency(dreData.results?.saldoFinal || 0)}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="dre-table-container">
              <table className="dre-table">
                <thead>
                  <tr>
                    <th className="category-column">Categoria</th>
                    <th className="value-column">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Renderizar categorias do modelo pessoal */}
                  {sortedMainCategories.map((category) => {
                    if (category === "NÃO CATEGORIZADO" || dreData.categories[category].total === 0) {
                      return null; // Skip rendering empty categories except non-categorized 
                    }
                    
                    return (
                      <React.Fragment key={category}>
                        <tr className="main-category">
                          <td>{category}</td>
                          <td className={getValueClass(dreData.categories[category].total || 0, true)}>
                            {formatCurrency(dreData.categories[category].total || 0)}
                          </td>
                        </tr>
                        
                        {/* Condicionalmente renderizar subcategorias com base no estado collapsedReport */}
                        {!collapsedReport && Object.entries(dreData.categories[category].items || {}).map(([subCategory, value], index) => (
                          <tr key={`${category}-${index}`} className="sub-category">
                            <td>{subCategory}</td>
                            <td className={getValueClass(value, true)}>
                              {formatCurrency(value)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* Linha de SALDO FINAL */}
                  <tr className="final-result-row">
                    <td>SALDO FINAL</td>
                    <td className={getValueClass(dreData.results?.saldoFinal || 0, true)}>
                      {formatCurrency(dreData.results?.saldoFinal || 0)}
                    </td>
                  </tr>
                  
                  {/* NÃO CATEGORIZADO */}
                  {Object.keys(dreData.categories?.["NÃO CATEGORIZADO"]?.items || {}).length > 0 && (
                    <>
                      <tr 
                        className="main-category non-categorized-header" 
                        onClick={toggleNonCategorizedSection}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          Transações não categorizadas 
                          <span className={`toggle-icon ${nonCategorizedExpanded ? 'toggle-icon-expanded' : ''}`}></span>
                        </td>
                        <td className={getValueClass(dreData.categories?.["NÃO CATEGORIZADO"]?.total || 0, true)}>
                          {formatCurrency(dreData.categories?.["NÃO CATEGORIZADO"]?.total || 0)}
                        </td>
                      </tr>
                      {!collapsedReport && nonCategorizedExpanded && Object.entries(dreData.categories?.["NÃO CATEGORIZADO"]?.items || {}).map(([description, value], index) => (
                        <tr key={`nao-categorizado-${index}`} className="sub-category non-categorized-item">
                          <td>{description.length > 50 ? description.substring(0, 50) + '...' : description}</td>
                          <td className={getValueClass(value, true)}>
                            {formatCurrency(value)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="dre-actions">
              <button className="print-button" onClick={() => window.print()}>
                Imprimir Relatório
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Renderização para modelos de negócio tradicionais
  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="dre-container">
        {/* Seletor de período com botão de colapsar no topo */}
        <div className="period-selector-container">
          <div className="period-selector-header">
            <h2>Demonstração do Resultado do Exercício (DRE)</h2>
            <div className="period-actions">
              <button 
                className={`collapse-button-top ${collapsedReport ? 'active' : ''}`} 
                onClick={toggleReportCollapse}
              >
                {collapsedReport ? 'Expandir' : 'Colapsar'}
              </button>
              
              <div className="period-dropdown">
                <label htmlFor="period-select">Período: </label>
                <select
                  id="period-select"
                  value={selectedPeriod}
                  onChange={(e) => {
                    const period = e.target.value;
                    setSelectedPeriod(period);
                    localStorage.setItem('selectedPeriod', period);
                  }}
                  disabled={dataLoading}
                >
                  {periods.length === 0 ? (
                    <option value="">Nenhum período disponível</option>
                  ) : (
                    periods.map((period) => (
                      <option key={period.value} value={period.value}>
                        {period.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {unmappedTransactions.length > 0 && (
          <div className="warning-message">
            <p>
              <strong>Atenção:</strong> Existem {unmappedTransactions.length} transações não 
              categorizadas neste período. Para uma DRE mais precisa, categorize estas transações na página de transações.
            </p>
          </div>
        )}
        
        {/* Botão de debug oculto - só visível se inspecionar ou clicar na área específica */}
        <div 
          onClick={toggleDebugMode} 
          style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px', 
            width: '20px', 
            height: '20px', 
            cursor: 'pointer',
            opacity: 0.2
          }}
        ></div>
        
        {debugMode && (
          <div className="debug-info" style={{ 
            margin: '10px 0', 
            padding: '10px', 
            background: '#f8f9fa', 
            border: '1px solid #ddd',
            fontSize: '12px'
          }}>
            <strong>Modo Debug Ativado</strong> - Verifique o console do navegador para detalhes
          </div>
        )}
        
        <div className="dre-content">
          <div className="dre-summary">
            <h2>Resumo Financeiro</h2>
            <div className="summary-cards">
              <div className="summary-card">
                <h3>Receita Bruta</h3>
                <div className={`summary-value ${getValueClass(dreData.results?.receitaBruta || 0, true)}`}>
                  {formatCurrency(dreData.results?.receitaBruta || 0)}
                </div>
              </div>
              
              <div className="summary-card">
                <h3>Lucro Bruto</h3>
                <div className={`summary-value ${getValueClass(dreData.results?.lucroBruto || 0, true)}`}>
                  {formatCurrency(dreData.results?.lucroBruto || 0)}
                </div>
              </div>
              
              <div className="summary-card">
                <h3>Resultado Líquido</h3>
                <div className={`summary-value ${getValueClass(dreData.results?.resultadoLiquido || 0, true)}`}>
                  {formatCurrency(dreData.results?.resultadoLiquido || 0)}
                </div>
              </div>
            </div>
          </div>
          
          <div className="dre-table-container">
            <table className="dre-table">
              <thead>
                <tr>
                  <th className="category-column">Categoria</th>
                  <th className="value-column">Valor</th>
                </tr>
              </thead>
              <tbody>
                {/* RECEITA */}
                {(dreData.categories?.["RECEITA"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>RECEITA</td>
                      <td className={getValueClass(dreData.categories?.["RECEITA"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["RECEITA"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["RECEITA"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`receita-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* DEDUÇÕES DA RECEITA */}
                {(dreData.categories?.["(-) DEDUÇÕES DA RECEITA"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>DEDUÇÕES DA RECEITA</td>
                      <td className={getValueClass(dreData.categories?.["(-) DEDUÇÕES DA RECEITA"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["(-) DEDUÇÕES DA RECEITA"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["(-) DEDUÇÕES DA RECEITA"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`deducoes-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* Linha de RECEITA LÍQUIDA */}
                {!collapsedReport && (
                  <tr className="result-row">
                    <td>RECEITA LÍQUIDA</td>
                    <td className={getValueClass(dreData.results?.receitaLiquida || 0, true)}>
                      {formatCurrency(dreData.results?.receitaLiquida || 0)}
                    </td>
                  </tr>
                )}
                
                {/* CUSTOS DAS MERCADORIAS VENDIDAS (CMV) OU CUSTOS DOS SERVIÇOS PRESTADOS (CSP) */}
                {((dreData.categories?.["(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"]?.total !== 0) || 
                (dreData.categories?.["(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)"]?.total !== 0)) && (
                  <>
                    <tr className="main-category">
                      <td>
                        {businessModel === "consultorioOdontologico"
                          ? "CUSTOS DOS SERVIÇOS PRESTADOS (CSP)"
                          : "CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"}
                      </td>
                      <td className={getValueClass(dreData.categories?.[businessModel === "consultorioOdontologico" ? "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)" : "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.[businessModel === "consultorioOdontologico" ? "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)" : "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.[businessModel === "consultorioOdontologico" ? "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)" : "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`cmv-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* Linha de LUCRO BRUTO */}
                {!collapsedReport && (
                  <tr className="result-row">
                    <td>LUCRO BRUTO</td>
                    <td className={getValueClass(dreData.results?.lucroBruto || 0, true)}>
                      {formatCurrency(dreData.results?.lucroBruto || 0)}
                    </td>
                  </tr>
                )}
                
                {/* OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS */}
                {(dreData.categories?.["(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS <em style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}>(não entra no cálculo do DRE)</em></td>
                      <td className={getValueClass(dreData.categories?.["(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`outras-receitas-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* DESPESAS OPERACIONAIS */}
                {(dreData.categories?.["(-) DESPESAS OPERACIONAIS"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>DESPESAS OPERACIONAIS</td>
                      <td className={getValueClass(dreData.categories?.["(-) DESPESAS OPERACIONAIS"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["(-) DESPESAS OPERACIONAIS"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["(-) DESPESAS OPERACIONAIS"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`despesas-operacionais-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* Linha de RESULTADO OPERACIONAL */}
                {!collapsedReport && (
                  <tr className="result-row">
                    <td>RESULTADO OPERACIONAL</td>
                    <td className={getValueClass(dreData.results?.resultadoOperacional || 0, true)}>
                      {formatCurrency(dreData.results?.resultadoOperacional || 0)}
                    </td>
                  </tr>
                )}
                
                {/* DESPESAS COM SÓCIOS */}
                {(dreData.categories?.["(-) DESPESAS COM SÓCIOS"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>DESPESAS COM SÓCIOS</td>
                      <td className={getValueClass(dreData.categories?.["(-) DESPESAS COM SÓCIOS"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["(-) DESPESAS COM SÓCIOS"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["(-) DESPESAS COM SÓCIOS"]?.items || {}).map(([subCategory, value], index) => {
                      // Check if the subcategory is the one the user is referring to
                      const isExcludedSubcategory = subCategory.toLowerCase() === 'despesas de sócios'; // Adjust this string if your subcategory name is slightly different

                      return (
                        <tr key={`despesas-socios-${index}`} className="sub-category">
                          <td>
                            {subCategory}
                            {isExcludedSubcategory && (
                              <span style={{ fontStyle: 'italic', color: '#777', marginLeft: '5px' }}>
                                {/* MODIFIED TEXT BELOW */}
                                (não entra no cálculo do DRE)
                              </span>
                            )}
                          </td>
                          <td className={getValueClass(value, true)}>
                            {formatCurrency(value)}
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
                
                {/* Linha de RESULTADO ANTES DO IR E CSL */}
                {!collapsedReport && (dreData.categories?.["(-) DEDUÇÕES DA RECEITA"]?.total !== 0) && (
                  <tr className="result-row">
                    <td>RESULTADO ANTES DO IR E CSL</td>
                    <td className={getValueClass(dreData.results?.resultadoAntesIR || 0, true)}>
                      {formatCurrency(dreData.results?.resultadoAntesIR || 0)}
                    </td>
                  </tr>
                )}
                
                {/* INVESTIMENTOS */}
                {(dreData.categories?.["(-) INVESTIMENTOS"]?.total !== 0) && (
                  <>
                    <tr className="main-category">
                      <td>INVESTIMENTOS</td>
                      <td className={getValueClass(dreData.categories?.["(-) INVESTIMENTOS"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["(-) INVESTIMENTOS"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && Object.entries(dreData.categories?.["(-) INVESTIMENTOS"]?.items || {}).map(([subCategory, value], index) => (
                      <tr key={`investimentos-${index}`} className="sub-category">
                        <td>{subCategory}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* Linha de RESULTADO LÍQUIDO */}
                <tr className="final-result-row">
                  <td>RESULTADO LÍQUIDO</td>
                  <td className={getValueClass(dreData.results?.resultadoLiquido || 0, true)}>
                    {formatCurrency(dreData.results?.resultadoLiquido || 0)}
                  </td>
                </tr>
                
                {/* NÃO CATEGORIZADO */}
                {Object.keys(dreData.categories?.["NÃO CATEGORIZADO"]?.items || {}).length > 0 && (
                  <>
                    <tr 
                      className="main-category non-categorized-header" 
                      onClick={toggleNonCategorizedSection}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        Transações não categorizadas 
                        <span className={`toggle-icon ${nonCategorizedExpanded ? 'toggle-icon-expanded' : ''}`}></span>
                      </td>
                      <td className={getValueClass(dreData.categories?.["NÃO CATEGORIZADO"]?.total || 0, true)}>
                        {formatCurrency(dreData.categories?.["NÃO CATEGORIZADO"]?.total || 0)}
                      </td>
                    </tr>
                    {!collapsedReport && nonCategorizedExpanded && Object.entries(dreData.categories?.["NÃO CATEGORIZADO"]?.items || {}).map(([description, value], index) => (
                      <tr key={`nao-categorizado-${index}`} className="sub-category non-categorized-item">
                        <td>{description.length > 50 ? description.substring(0, 50) + '...' : description}</td>
                        <td className={getValueClass(value, true)}>
                          {formatCurrency(value)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="dre-actions">
            <button className="print-button" onClick={() => window.print()}>
              Imprimir DRE
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DREReport;